import React, { useState, useCallback, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import axios from "axios";
import { useRouter } from "next/router";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { ENDPOINTS } from "@/config/endpoints";
import { pushCleverTapEvent } from "../pushCleverTapEvent";
import enIN from "@/locales/global-buyer/enIN";
import esMX from "@/locales/global-buyer/esMX";
import enUS from "@/locales/global-buyer/enUS";

const schema = (t) => {
  const requiredMessage = t.FormData.requiredMessage;
  const emailMessage = t.FormData.emailMessage;
  const phoneMessage = t.FormData.phoneMessage;

  return yup.object({
    country: yup
      .string()
      .required(requiredMessage)
      .notOneOf([""], requiredMessage),
    Email: yup.string().required(requiredMessage).email(emailMessage),
    Phone: yup
      .string()
      .required(requiredMessage)
      .matches(/^[0-9+()-]*$/, phoneMessage),
    Company: yup.string().required(requiredMessage),
  });
};

const GlobalBuyerForm = () => {
  const router = useRouter();
  const { locale } = router;
  const t =
    (locale === "es-mx" && esMX) ||
    (locale === "en-us" && enUS) ||
    (locale === "en-in" && enIN);
  const [campaignName, setCampaignName] = useState("GB_INDIA");
  const validationSchema = schema(t);
  const [ThankyouRes, setThankyouRes] = useState(false);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [errMSG, setErrMSG] = useState(false);
  const countryDropdownRef = useRef(null);
  const {
    register,
    handleSubmit,
    formState: { isValid, errors, reset },
    watch,
    setValue,
  } = useForm({
    mode: "all",
    resolver: yupResolver(validationSchema),
  });
  const watchAllFields = watch();

  const trackEventClick = () => {
    if (locale === "en-in") {
      pushCleverTapEvent("Global_Buyer_Form_Start", {
        PageName: "Global_Buyer_Page",
      });
    }
  };
  const options = [
    { label: "$100k-$500k", value: "100000-500000" },
    { label: "$500k-$1M", value: "500000-1000000" },
    { label: "$1M", value: "0-1000000" },
  ];
  const [activeOption, setActiveOption] = useState(options[0].value);
  const handleClick = (value) => {
    setActiveOption(value);
  };
  const activeOptionValue = activeOption.split("-");
  const minValue = activeOptionValue[0];
  const maxValue = activeOptionValue[1];

  const [countryCode, setCountryCode] = useState("");
  const [countryInput, setCountryInput] = useState("");
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [filteredCountries, setFilteredCountries] = useState(t.country_Name);
  const [showCountryCode, setShowCountryCode] = useState(false);

  const handleCountryInputChange = (e) => {
    const value = e.target.value;
    setCountryInput(value);
    setShowCountryDropdown(true);

    if (value.trim() === "") {
      setFilteredCountries(t.country_Name);
    } else {
      const filtered = t.country_Name.filter((country) =>
        country.name.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredCountries(filtered);
    }
  };

  const handleCountrySelect = (country) => {
    setCountryInput(country.name);
    setShowCountryDropdown(false);
    setCountryCode(`+${country.code}`);
    setShowCountryCode(true);
    setValue("country", country.name, { shouldValidate: true });
  };

  const handleCaptchaSubmit = useCallback(
    (values) => {
      if (!executeRecaptcha) {
        return false;
      }
      executeRecaptcha().then((gReCaptchaToken) => {
        onFormSubmit(
          { ...values, campaignName, minValue, maxValue },
          gReCaptchaToken
        );
      });
    },
    [executeRecaptcha, campaignName, minValue, maxValue]
  );

  useEffect(() => {
    if (locale === "en-in") {
      setCampaignName("GB_INDIA");
    } else if (locale === "es-mx") {
      setCampaignName("GB_MEXICO");
    } else if (locale === "en-us") {
      setCampaignName("GB_INDIA");
    }
  }, [locale]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        countryDropdownRef.current &&
        !countryDropdownRef.current.contains(event.target)
      ) {
        setShowCountryDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const floatingLabelClass = (fieldValue) => `
  absolute left-3 bg-white text-sm text-gray-500 font-normal transition-all pointer-events-none
  ${
    fieldValue
      ? "top-[-8px] text-xs text-black px-xsp"
      : "top-2 peer-focus:top-[-8px] peer-focus:text-xs peer-focus:text-black px-xsp"
  }
`;

  async function onFormSubmit(values, gReCaptchaToken) {
    const body = {
      formType: "GlobalBuyerForm",
      country: values.country,
      email: values.Email,
      phoneNumber: values.Phone,
      company: values.Company,
      sourceUrl: window.location.href,
      campaignName: values.campaignName,
      currencyOfFundingRequirement: "USD",
      minimumFundingRequiredAmnt: values.minValue,
      maximumFundingRequiredAmnt: values.maxValue,
    };
    let config = {
      method: "post",
      url: ENDPOINTS.GlobalBuyers,
      headers: {
        "Content-Type": "application/json",
        "g-recaptcha-response": gReCaptchaToken,
      },
      data: body,
    };
    try {
      const response = await axios(config);
      if (response.status == 200) {
        if (locale === "es-mx") {
          gtag("event", "getGlobalMexico", {
            event_category: "getGlobalMexico",
            event_label: "Global Mexico",
          });
        } else if (locale === "en-in") {
          gtag("event", "getGlobalDomestic", {
            event_category: "getGlobalDomestic",
            event_label: "Global Domestic",
          });
        } else if (locale === "en-us") {
          gtag("event", "getGlobalUS", {
            event_category: "getGlobalUS",
            event_label: "Global US",
          });
        }
        if (locale === "en-in") {
          pushCleverTapEvent("Global_Buyer_Form_Submit", {
            PageName: "Global_Buyer_Page",
            ...body,
          });
        }
        setThankyouRes(true);
        setTimeout(() => {
          setThankyouRes(false);
          router.push("/thankyou-response");
        }, 5000);
      }
    } catch (err) {
      setErrMSG(true);
      setTimeout(() => {
        window.location.reload();
      }, 3000);
    }
  }

  return (
      <div className="bg-white/95 border-[#D7D7DC] border-[1px] rounded-[8px] py-4 px-3 sm:px-2 lg:w-full lg:max-w-[400px] md:max-w-[360px] sm:w-full sm:max-w-full ml-auto md:m-auto border-#FFFFFF">
        <p
          className="text-lg	sm:text-base flex text-[#012A5B] mb-xsp font-medium"
          dangerouslySetInnerHTML={{ __html: t.FormData.Heading }}
        />
        <form
          onSubmit={handleSubmit(handleCaptchaSubmit)}
          // onSubmit={handleSubmit(onFormSubmit)}
        >
          <div className="relative mt-2">
            <input
              {...register("Company")}
              id="company"
              type="text"
              placeholder=" "
              className="peer block w-full rounded-md border border-[#D9D9D9] px-3 py-2 text-sm focus:border-[#288BED] focus:outline-none"
              onClick={trackEventClick}
            />
            <label
              htmlFor="company"
              className={floatingLabelClass(watchAllFields.Company)}
            >
              {t.FormData.Company}
            </label>
            <p className="text-progressColor4 text-xs mt-[3px]">
              {errors.Company?.message}
            </p>
          </div>

          <div className="relative mt-2">
            <input
              {...register("Email")}
              id="email"
              type="email"
              placeholder=" "
              className="peer block w-full rounded-md border border-[#D9D9D9] px-3 py-2 text-sm focus:border-[#288BED] focus:outline-none"
            />
            <label
              htmlFor="email"
              className={floatingLabelClass(watchAllFields.Email)}
            >
              {t.FormData.Email}
            </label>
            <p className="text-progressColor4 text-xs mt-[3px]">
              {errors.Email?.message}
            </p>
          </div>

          <div className="mt-2 relative" ref={countryDropdownRef}>
            <input
              {...register("country")}
              id="country"
              type="text"
              value={countryInput}
              onChange={handleCountryInputChange}
              onFocus={() => setShowCountryDropdown(true)}
              placeholder=" "
              className="peer block w-full rounded-md border border-[#D9D9D9] px-3 py-2 text-sm bg-white cursor-pointer"
              autoComplete="off"
            />
            <label
              htmlFor="country"
              className={floatingLabelClass(countryInput)}
            >
              {t.FormData.CountryName}
            </label>
            <div className="absolute inset-y-0 right-2 flex items-center pointer-events-none">
              <svg
                className="w-4 h-4 text-gray-400 mt-xsp"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4 4a.75.75 0 01-1.06 0l-4-4a.75.75 0 01.02-1.06z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            {showCountryDropdown && filteredCountries.length > 0 && (
              <div className="absolute z-10 w-full mt-0 bg-white border border-[#D9D9D9] rounded-md shadow-lg max-h-60 overflow-y-auto">
                {filteredCountries.map((country) => (
                  <div
                    key={country.id}
                    onClick={() => handleCountrySelect(country)}
                    className="px-3 py-2 text-sm cursor-pointer hover:bg-gray-100"
                  >
                    {country.name}
                  </div>
                ))}
              </div>
            )}
          </div>
          <p className="text-progressColor4 text-xs mt-[3px]">
            {errors.country?.message}
          </p>
          <div className="relative mt-2">
            <div className="flex rounded-md border border-[#D9D9D9] focus-within:border-[#288BED]">
              {showCountryCode && (
                <div className="w-[80px] text-sm py-2 rounded-l-md text-center text-black ">
                  {" "}
                  {countryCode}{" "}&nbsp;|
                </div>
              )}
              <input
                {...register("Phone")}
                id="phone"
                type="number"
                placeholder={t.FormData.Mobile}
                className={`peer block w-full py-2 text-sm focus:outline-none
                  ${showCountryCode ? " pr-3 rounded-r-md " : "rounded-md px-3"}
                `}
                onFocus={() => setShowCountryCode(true)}
              />
            </div>
            <label
              htmlFor="phone"
              className={`${floatingLabelClass(
                showCountryCode || watchAllFields.Phone
              )} ${showCountryCode ? "left-0" : "left-3"}`}
            >
              {t.FormData.Mobile}
            </label>
            <p className="text-progressColor4 text-xs mt-[3px]">
              {errors.Phone?.message}
            </p>
          </div>

          <div className="relative mt-3">
            <label className={floatingLabelClass(activeOption)}>
              {t.FormData.SelectFunding}
            </label>

            <div className="flex rounded-md border border-[#3B82F6] p-[8px] justify-around ">
              {options.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleClick(option.value)}
                  className={`flex rounded-md px-1 py-xsp transition-all mt-xsp text-xs text-black
          ${
            activeOption === option.value
              ? "bg-[#E6F0FF] border border-[#3B82F6] text-[#0362C2] px-1"
              : "text-black hover:bg-gray-50 text-xs"
          }
        `}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-4">
            <button
              className="py-2 px-3 rounded-md text-white text-base font-medium w-full bg-gradient-to-r from-[#022B5C] to-[#0363C4] shadow-lg transition-all duration-300 hover:from-[#0363C4] hover:to-[#022B5C]"
              type="submit"
            >
              {t.FormData.Submitbtn}
            </button>
          </div>
        </form>
        {ThankyouRes ? (
          <div className="text-green text-center">
            <p className="text-sm font-medium mt-3">{t.FormData.ThankYou}</p>
          </div>
        ) : null}
      </div>
  );
};

export default GlobalBuyerForm;
